/**
 * Клиент API. Единственное место, где проект обращается к сети.
 * Все изменяющие запросы несут заголовок X-Requested-With — защита от CSRF.
 */
import type {
  ApiError, BootstrapResponse, Brand, BrandChannelPatch, BrandsResponse, Category,
  ChangeSetDetail, ChangeSetSummary, ChannelCode, Comment, CommitResponse, Conflict,
  CommentKind, CopilotChat, CopilotMessage,
  Draft, DraftPreview, EnumValue, FieldDefinition, MutationResponse, RevisionResponse, SavedView, UserSummary,
} from '@shared/types.ts'

export class ApiRequestError extends Error {
  readonly code: ApiError['error']['code']
  readonly status: number
  readonly details: unknown

  constructor(status: number, body: ApiError['error']) {
    super(body.message)
    this.status = status
    this.code = body.code
    this.details = body.details
  }

  /** Конфликты подтверждения приходят в details. */
  get conflicts(): Conflict[] {
    return Array.isArray(this.details) ? (this.details as Conflict[]) : []
  }
}

/**
 * Текущая пользовательская операция. Массовая простановка шлёт по запросу на
 * бренд; сервер ставит этот идентификатор всем записанным правкам, и «Шаг
 * назад» снимает их одним движением, а не по одной.
 */
let operationId: string | null = null

/** Пометить всё, что уйдёт на сервер внутри `fn`, одной операцией. */
export async function asOneOperation<T>(fn: () => Promise<T>): Promise<T> {
  const previous = operationId
  /* Колонка operation_id в базе — uuid, поэтому и здесь настоящий UUID. */
  operationId = crypto.randomUUID()
  try {
    return await fn()
  } finally {
    operationId = previous
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'X-Requested-With': 'bm' }
  if (operationId) headers['X-Operation-Id'] = operationId
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (res.status === 204) return undefined as T
  const text = await res.text()
  const parsed: unknown = text ? JSON.parse(text) : null

  if (!res.ok) {
    const err = (parsed as ApiError | null)?.error
    throw new ApiRequestError(res.status, err ?? { code: 'internal', message: 'Сервер не ответил. Проверьте соединение.' })
  }
  return parsed as T
}


/**
 * Поток ответа копилота (SSE поверх обычного POST).
 *
 * EventSource здесь не годится: он умеет только GET, а вопрос уходит телом
 * запроса. Поэтому читаем тело ответа кусками сами.
 */
async function copilotStream(
  input: { question: string; chatId?: number },
  onText: (piece: string) => void,
  onReason?: (piece: string) => void,
): Promise<{ chatId: number; title: string; brands: number }> {
  const res = await fetch('/api/copilot/ask', {
    method: 'POST',
    headers: { 'X-Requested-With': 'bm', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  })

  if (!res.ok || !res.body) {
    const text = await res.text()
    let parsed: ApiError | null = null
    try { parsed = text ? (JSON.parse(text) as ApiError) : null } catch { parsed = null }
    throw new ApiRequestError(res.status, parsed?.error ?? {
      code: 'internal', message: 'Сервер не ответил. Проверьте соединение.',
    })
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let tail = ''
  let meta: { chatId: number; title: string; brands: number } | null = null

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    tail += decoder.decode(value, { stream: true })
    const lines = tail.split('\n')
    tail = lines.pop() ?? ''
    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed.startsWith('data:')) continue
      const payload = trimmed.slice(5).trim()
      if (payload === '') continue
      const event = JSON.parse(payload) as {
        text?: string; reason?: string; error?: string; done?: boolean
        chatId?: number; title?: string; brands?: number
      }
      if (event.error) throw new ApiRequestError(500, { code: 'internal', message: event.error })
      if (typeof event.reason === 'string') onReason?.(event.reason)
      if (typeof event.text === 'string') onText(event.text)
      if (typeof event.chatId === 'number') {
        meta = { chatId: event.chatId, title: event.title ?? '', brands: event.brands ?? 0 }
      }
    }
  }

  if (!meta) throw new ApiRequestError(500, { code: 'internal', message: 'Ответ не получен' })
  return meta
}

const get = <T,>(path: string) => request<T>('GET', path)
const post = <T,>(path: string, body?: unknown) => request<T>('POST', path, body ?? {})
const patch = <T,>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {})
const del = <T,>(path: string) => request<T>('DELETE', path)

export const api = {
  // ── вход ──
  loginByCode: (code: string) =>
    post<{ user: BootstrapResponse['user'] }>('/api/auth/code', { code }),
  setLoginCode: (userId: number, code?: string) =>
    post<{ user: unknown }>(`/api/users/${userId}/login-code`, code ? { code } : {}),
  revokeLoginCode: (userId: number) => del<{ user: unknown }>(`/api/users/${userId}/login-code`),
  login: (email: string, password: string) =>
    post<{ user: BootstrapResponse['user'] }>('/api/auth/login', { email, password }),
  logout: () => post<void>('/api/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<void>('/api/auth/password', { currentPassword, newPassword }),

  // ── данные ──
  bootstrap: () => get<BootstrapResponse>('/api/bootstrap'),
  brands: (status: 'active' | 'archived' | 'all' = 'active') =>
    get<BrandsResponse>(`/api/brands?status=${status}`),
  brand: (id: number) => get<{ brand: Brand }>(`/api/brands/${id}`),
  revision: () => get<RevisionResponse>('/api/revision'),
  comments: (brandId: number) => get<{ comments: Comment[] }>(`/api/brands/${brandId}/comments`),
  brandHistory: (brandId: number) => get<{ changes: ChangeSetDetail['changes'] }>(`/api/brands/${brandId}/history`),

  // ── правки (пишут в черновик) ──
  createBrand: (input: {
    name: string
    categoriesUnclear?: boolean
    channels?: Partial<Record<ChannelCode, BrandChannelPatch>>
    categoryIds?: number[]
    fields?: Record<string, unknown>
  }) => post<MutationResponse>('/api/brands', input),

  updateBrand: (id: number, input: { name?: string; categoriesUnclear?: boolean; fields?: Record<string, unknown> }) =>
    patch<MutationResponse>(`/api/brands/${id}`, input),

  updateChannel: (id: number, channel: ChannelCode, input: BrandChannelPatch) =>
    patch<MutationResponse>(`/api/brands/${id}/channels/${channel}`, input),

  attachCategory: (brandId: number, categoryId: number, channels?: Partial<Record<ChannelCode, { tier?: string | null }>>) =>
    post<MutationResponse>(`/api/brands/${brandId}/categories`, { categoryId, channels }),

  detachCategory: (brandId: number, brandCategoryId: number) =>
    del<MutationResponse>(`/api/brands/${brandId}/categories/${brandCategoryId}`),

  updateCategoryChannel: (
    brandId: number, brandCategoryId: number, channel: ChannelCode,
    input: { isActive?: boolean; tier?: string | null },
  ) => patch<MutationResponse>(`/api/brands/${brandId}/categories/${brandCategoryId}/channels/${channel}`, input),

  updateCategoryFields: (brandId: number, brandCategoryId: number, fields: Record<string, unknown>) =>
    patch<MutationResponse>(`/api/brands/${brandId}/categories/${brandCategoryId}`, { fields }),

  archiveBrand: (id: number) => post<MutationResponse>(`/api/brands/${id}/archive`),
  restoreBrand: (id: number) => post<MutationResponse>(`/api/brands/${id}/restore`),

  addComment: (brandId: number, input: {
    body: string
    channelCode?: ChannelCode | null
    brandCategoryId?: number | null
    kind?: CommentKind
    dueOn?: string | null
    replyTo?: number | null
  }) => post<MutationResponse>(`/api/brands/${brandId}/comments`, input),

  /** Закрыть задачу: галочка и обязательный отчёт одним запросом. */
  completeTask: (commentId: number, body: string) =>
    post<MutationResponse>(`/api/comments/${commentId}/done`, { body }),

  /** Перевести заметку в задачу и обратно. */
  setCommentKind: (commentId: number, kind: CommentKind, dueOn?: string | null) =>
    patch<MutationResponse>(`/api/comments/${commentId}/kind`, { kind, dueOn: dueOn ?? null }),
  editComment: (id: number, body: string) => patch<MutationResponse>(`/api/comments/${id}`, { body }),
  deleteComment: (id: number) => del<MutationResponse>(`/api/comments/${id}`),

  // ── черновик ──
  draft: () => get<Draft>('/api/draft'),
  setDraftNote: (note: string) => patch<{ note: string }>('/api/draft', { note }),
  /** Текст, который уйдёт в чат: собирает сервер — тем же кодом, что отправку. */
  draftPreview: (note: string) =>
    get<DraftPreview>(`/api/draft/preview${note ? `?note=${encodeURIComponent(note)}` : ''}`),
  dropChange: (changeId: number) => del<MutationResponse>(`/api/draft/changes/${changeId}`),
  discardDraft: () => del<void>('/api/draft'),
  commit: (input?: { note?: string; resolutions?: { changeId: number; decision: 'mine' | 'theirs' | 'drop' }[] }) =>
    post<CommitResponse>('/api/draft/commit', input ?? {}),

  // ── история ──
  changeSets: (params: Record<string, string | number | undefined> = {}) => {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v))
    return get<{ items: ChangeSetSummary[]; total: number }>(`/api/change-sets?${qs.toString()}`)
  },
  changeSet: (id: number) => get<ChangeSetDetail>(`/api/change-sets/${id}`),
  revert: (id: number) => post<CommitResponse>(`/api/change-sets/${id}/revert`),
  notifyAgain: (id: number) => post<{ status: string }>(`/api/change-sets/${id}/notify`),

  // ── справочники и администрирование ──
  categories: () => get<{ items: Category[] }>('/api/categories'),
  createCategory: (name: string) => post<{ category: Category }>('/api/categories', { name }),
  renameCategory: (id: number, name: string) => patch<{ category: Category }>(`/api/categories/${id}`, { name }),
  archiveCategory: (id: number) => post<void>(`/api/categories/${id}/archive`),
  mergeCategories: (id: number, targetId: number) => post<void>(`/api/categories/${id}/merge`, { targetId }),

  enums: () => get<{ sets: Record<string, EnumValue[]> }>('/api/enums'),
  createEnumValue: (setCode: string, input: Partial<EnumValue>) =>
    post<{ value: EnumValue }>(`/api/enums/${setCode}/values`, input),
  /** Новый справочник: нужен для колонки со своим набором значений. */
  createEnumSet: (input: { code: string; name: string; hint?: string | null }) =>
    post<{ code: string; name: string }>('/api/enums', input),

  updateEnumValue: (setCode: string, id: number, input: Partial<EnumValue>) =>
    patch<{ value: EnumValue }>(`/api/enums/${setCode}/values/${id}`, input),

  /**
   * Копилот: вопрос по таблице. Ключ модели живёт на сервере.
   *
   * Ответ приходит потоком: буквы появляются сразу, а не через минуту
   * молчания. `onText` зовётся на каждый кусок, итог — когда поток закрылся.
   */
  copilotAsk: (
    input: { question: string; chatId?: number },
    onText: (piece: string) => void,
    onReason?: (piece: string) => void,
  ) => copilotStream(input, onText, onReason),

  /** Свои разговоры с копилотом: к разбору возвращаются через день-другой. */
  copilotChats: () =>
    get<{ chats: CopilotChat[] }>('/api/copilot/chats'),

  copilotChat: (id: number) =>
    get<{ chat: CopilotChat; messages: CopilotMessage[] }>(`/api/copilot/chats/${id}`),

  copilotDeleteChat: (id: number) => del<{ ok: true }>(`/api/copilot/chats/${id}`),

  fields: () => get<{ items: FieldDefinition[] }>('/api/fields'),
  createField: (input: Partial<FieldDefinition>) => post<{ field: FieldDefinition }>('/api/fields', input),
  updateField: (id: number, input: Partial<FieldDefinition>) => patch<{ field: FieldDefinition }>(`/api/fields/${id}`, input),
  archiveField: (id: number) => del<void>(`/api/fields/${id}`),
  /** Своё название колонки или блока таблицы. */
  renameTableTitle: (input: { kind: 'column' | 'group' | 'move'; code: string; title: string }) =>
    patch<{ ok: true }>('/api/table/titles', input),

  users: () => get<{ items: UserSummary[] }>('/api/users'),
  createUser: (input: { fullName: string; shortName: string; email: string; jobTitle?: string; roleCode: string }) =>
    post<{ user: UserSummary; password: string }>('/api/users', input),
  updateUser: (id: number, input: Partial<{
    fullName: string; shortName: string; jobTitle: string; roleCode: string
    isActive: boolean; color: string | null
  }>) =>
    patch<{ user: UserSummary }>(`/api/users/${id}`, input),
  resetUserPassword: (id: number) => post<{ password: string }>(`/api/users/${id}/reset-password`),
  roles: () => get<{ items: { id: number; code: string; name: string; permissions: string[] }[] }>('/api/roles'),

  settings: () => get<{ settings: Record<string, unknown> }>('/api/settings'),
  updateSettings: (input: Record<string, unknown>) => patch<{ settings: Record<string, unknown> }>('/api/settings', input),
  telegramTest: () => post<{ ok: boolean; message: string }>('/api/settings/telegram/test'),
  telegramStatus: () => get<{ enabled: boolean; configured: boolean; chatTitle?: string; lastError?: string | null; pendingCount: number; failedCount: number }>('/api/settings/telegram/status'),

  views: () => get<{ items: SavedView[] }>('/api/views'),
  createView: (input: { name: string; isShared: boolean; config: SavedView['config'] }) =>
    post<{ view: SavedView }>('/api/views', input),
  updateView: (id: number, input: Partial<{ name: string; isShared: boolean; config: SavedView['config'] }>) =>
    patch<{ view: SavedView }>(`/api/views/${id}`, input),
  deleteView: (id: number) => del<void>(`/api/views/${id}`),

  exportCsvUrl: () => '/api/export.csv',
  exportXlsxUrl: () => '/api/export.xlsx',
}
